import { SpineEvents } from './../../core/enums/SpineEvents';

export const VehicleSpines = {
  BamPrank: 'bam-prank',
  BibiJestah: 'bibi-jestah',
  BatwingQuizz: 'batwing-quizz',
  RedbirdDucky: 'redbird-ducky',
  BuffSnowy: 'buff-snowy'
} as const;
export type VehicleSpinesKeys = keyof typeof VehicleSpines;
export type VehicleSpinesValues = typeof VehicleSpines[VehicleSpinesKeys];

export const VehicleSkins = {
  Bam: 'bam',
  Prank: 'prank',
  Bibi: 'bibi',
  Jestah: 'jestah',
  Batwing: 'batwing',
  Quizz: 'quizz',
  Redbird: 'redbird',
  Ducky: 'ducky',
  Buff: 'buff',
  Snowy: 'snowy'
} as const;
export type VehicleSkinsKeys = keyof typeof VehicleSkins;
export type VehicleSkinsValues = typeof VehicleSkins[VehicleSkinsKeys];

export const CharacterAnimation = {
  Boost: 'boost',
  Bounce: 'bounce',
  Impact: 'impact',
  Slide: 'slide',
  Start: 'start',
  Static: 'static',

  Idle: 'idle',
  Drive: 'wheels_spin',
  HeroCharge: 'hero_charge',
  HeroRampIn: 'hero_ramp_in',
  HeroRampAir: 'hero_ramp_in2',
  HeroRampOut: 'hero_ramp_out',
  Warning: 'warning',
  WeaponIdle: 'weapon_idle',
  Weapon: 'weapon',
  HeroRecoil: 'hero_recoil',
  Blink: 'blink',
  HeroYellowStrip: 'hero_yellow_strip',
  HeroPull: 'hero_mag_pull', // Hero Pull. Hero version of VillainPulled.
  VillainPulled: 'villain_pulled',
  HeroScreech: 'hero_screech',
  VillainScreech: 'villian_screech'
} as const;

export type CharacterAnimationKeys = keyof typeof CharacterAnimation;
export type CharacterAnimationValues = typeof CharacterAnimation[CharacterAnimationKeys];

export const CharacterState = {
  Idle: 'idle',
  Driving: 'driving',
  RampIn: 'rampIn',
  RampOut: 'rampOut'
} as const;

export type CharacterStateKeys = keyof typeof CharacterState;
export type CharacterStateValues = typeof CharacterState[CharacterStateKeys];

// Types aren't correct - https://github.com/photonstorm/phaser/issues/5463
export default class CharacterSpine extends window.SpinePlugin.SpineGameObject {
  // Find the correct spine file for each skin.
  public static skinsToSpine: Map<VehicleSkinsValues, VehicleSpinesValues> = new Map([
    [VehicleSkins.Bam, VehicleSpines.BamPrank],
    [VehicleSkins.Prank, VehicleSpines.BamPrank],
    [VehicleSkins.Bibi, VehicleSpines.BibiJestah],
    [VehicleSkins.Jestah, VehicleSpines.BibiJestah],
    [VehicleSkins.Batwing, VehicleSpines.BatwingQuizz],
    [VehicleSkins.Quizz, VehicleSpines.BatwingQuizz],
    [VehicleSkins.Redbird, VehicleSpines.RedbirdDucky],
    [VehicleSkins.Ducky, VehicleSpines.RedbirdDucky],
    [VehicleSkins.Buff, VehicleSpines.BuffSnowy],
    [VehicleSkins.Snowy, VehicleSpines.BuffSnowy],
  ]);

  public static events = {
    RampInEnded: 'RampInEnded',
    RampAirEnded: 'RampAitEnded',
    RampOutEnded: 'RampOutEnded',
    WarningEnded: 'WarningEnded'
  } as const;

  protected spineTracks: Map<CharacterAnimationValues, spine.TrackEntry>;
  protected skins: VehicleSkinsValues[];
  protected currentSkinIndex: number;
  protected isMoving: boolean;
  protected currentState: CharacterStateValues;

  constructor(scene: Phaser.Scene, x: number, y: number, skin: VehicleSkinsValues) {
    super(scene, scene.sys.spine, x, y, CharacterSpine.skinsToSpine.get(skin), CharacterAnimation.Static);

    this.spineTracks = new Map();

    this.skins = Object.values(VehicleSkins);
    this.currentSkinIndex = this.skins.indexOf(skin);
    this.setSkinByName(skin);
    this.scale = 1;

    this.isMoving = false;
    this.currentState = CharacterState.Idle;

    // this.preMultipliedAlpha = true;

    // this.setMix(CharacterAnimation.HeroRampAir, CharacterAnimation.HeroRampOut, 0);

    // this.scene.events.on(Phaser.Scenes.Events.UPDATE, this.update, this);
    this.once(Phaser.GameObjects.Events.DESTROY, this.onDestroy, this);
    // this.scene.events.once(CharacterSpine.events.StopCharacter, this.stopMoving, this);

    this.on(SpineEvents.Start, this.animationStarted, this);
    this.on(SpineEvents.Complete, this.animationCompleted, this);
    this.on(SpineEvents.End, this.animationEnded, this);
    this.on(SpineEvents.Interrupted, this.animationInterrupted, this);
    this.on(SpineEvents.Dispose, this.animationDisposed, this);
    this.on(SpineEvents.Event, this.animationEvent, this);
  }

  get CurrentSkin() {
    return this.skins[this.currentSkinIndex % this.skins.length];
  }

  public switchSkin(skin: VehicleSkinsValues) {
    if (this.findSkin(skin)) {
      this.setSkinByName(skin);
      this.currentSkinIndex = this.skins.indexOf(skin);
    } else {
      // @ts-ignore - Phaser doc is wrong on parameters. Fixed in beta version by this commit: https://github.com/photonstorm/phaser/commit/3162ba63d97a6808928fdee7c93b2c69af1353c0
      this.setSkeleton(CharacterSpine.skinsToSpine.get(skin));
      this.setSkinByName(skin);
      this.currentSkinIndex = this.skins.indexOf(skin);
    }
  }

  public cycleCharacter() {
    this.currentSkinIndex++;
    this.setSkinByName(this.skins[this.currentSkinIndex % this.skins.length]);
  }

  public startMoving() {
    this.isMoving = true;
    this.drive(false);
    // this.setAnimation(0, CharacterAnimation.Start, false);
    // this.once('end', () => {
    //   this.drive();
    // });
  }

  public stopMoving() {
    this.isMoving = false;
    // this.setAnimation(0, CharacterAnimation.Impact, false);
    // this.once(SpineEvents.End, () => {
    //   this.addAnimation(0, CharacterAnimation.Static, true);
    // });
  }

  // #region animations

  public drive(waitForEnd: boolean) {
    this.currentState = CharacterState.Driving;
    if (waitForEnd) {
      let track = this.addAnimation(0, CharacterAnimation.Drive, true);
      this.spineTracks.set(CharacterAnimation.Drive, track);
      track.timeScale = 2;

      this.setAnimation(1, CharacterAnimation.Blink, false);
    } else {
      let track = this.setAnimation(0, CharacterAnimation.Drive, true);
      this.spineTracks.set(CharacterAnimation.Drive, track);
      track.timeScale = 2;

      this.setAnimation(1, CharacterAnimation.Blink, false);
    }
  }

  public setDriveSpeed(speed: number) {
    if (this.spineTracks.has(CharacterAnimation.Drive)) {
      let trackEntry = this.spineTracks.get(CharacterAnimation.Drive);
      if (trackEntry) trackEntry.timeScale = speed / 6;
    }
  }

  public heroCharge() {
    this.setAnimation(3, CharacterAnimation.HeroCharge);
  }

  public rampIn(speed: number, callback?: () => void) {
    let track = this.setAnimation(2, CharacterAnimation.HeroRampIn, false);
    // track.timeScale = 1.25;
    track.timeScale = speed * 1.5625;
    this.addAnimation(2, CharacterAnimation.HeroRampAir, false);
    this.once(SpineEvents.End, (entry: spine.TrackEntry) => {
      callback?.();
    });
  }

  public rampOut(speed: number, callback?: () => void) {
    let track = this.setAnimation(2, CharacterAnimation.HeroRampOut, false);
    // track.timeScale = 2.25;
    track.timeScale = speed * 2.8125;
    this.state.addEmptyAnimation(2, 0, 0);
    this.once(SpineEvents.End, () => {
      callback?.();
    });
  }

  public showBelt() {
    this.setAnimation(2, CharacterAnimation.HeroYellowStrip, false);
  }

  public heroPull() {
    this.setAnimation(2, CharacterAnimation.HeroPull, false);
    this.state.setEmptyAnimation(0, 0);
  }

  public villainPulled() {
    this.setAnimation(2, CharacterAnimation.VillainPulled, false);
    this.state.setEmptyAnimation(0, 0);
  }

  public heroTireScreech() {
    this.setAnimation(3, CharacterAnimation.HeroScreech, false);
  }

  public villainTireScreech() {
    this.setAnimation(3, CharacterAnimation.VillainScreech, false);
  }

  public warning() {
    this.setAnimation(2, CharacterAnimation.Warning, false);
    this.state.addEmptyAnimation(2, 1, 0);
  }

  public fireEquipment() {
    this.setAnimation(2, CharacterAnimation.Weapon, false);
    this.addAnimation(2, CharacterAnimation.WeaponIdle, false);
  }

  public warningAndFire() {
    this.setAnimation(2, CharacterAnimation.Warning, false);
    this.addAnimation(2, CharacterAnimation.Weapon, false);
    this.addAnimation(2, CharacterAnimation.WeaponIdle, false);
    // this.state.addEmptyAnimation(0, 1, 0);
  }

  public cancelFire() {
    // let currentAnimation = this.getCurrentAnimation(2);
    // let isInProgress = currentAnimation.name == CharacterAnimation.Warning || currentAnimation.name == CharacterAnimation.Weapon;

    // if (isInProgress) this.clearTrack(2);
    this.setAnimation(2, CharacterAnimation.WeaponIdle, false);
  }

  public hitRecoil() {
    this.setAnimation(2, CharacterAnimation.HeroRecoil, false);
    this.state.addEmptyAnimation(2,0,0);
  }

  // public boost() {
  //   this.setAnimation(0, CharacterAnimation.Boost, false);
  //   this.once(SpineEvents.End, () => {
  //     if (this.isMoving) this.drive(true);
  //   });
  // }

  // public bounce() {
  //   this.setAnimation(0, CharacterAnimation.Bounce, false);
  //   this.once(SpineEvents.End, () => {
  //     if (this.isMoving) this.drive(true);
  //   });
  // }

  // public slide() {
  //   this.setAnimation(0, CharacterAnimation.Slide, false);
  //   this.once(SpineEvents.End, () => {
  //     if (this.isMoving) this.drive(true);
  //   });
  // }

  // public impact() {
  //   this.setAnimation(0, CharacterAnimation.Impact, false);
  //   this.once(SpineEvents.End, () => {
  //     if (this.isMoving) this.drive(true);
  //   });
  // }

  // #endregion

  public onDestroy(): void {
    // this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.update, this);

    this.off(SpineEvents.Start, this.animationStarted, this);
    this.off(SpineEvents.Complete, this.animationCompleted, this);
    this.off(SpineEvents.End, this.animationEnded, this);
    this.off(SpineEvents.Interrupted, this.animationInterrupted, this);
    this.off(SpineEvents.Dispose, this.animationDisposed, this);
  }

  protected animationStarted(entry: spine.TrackEntry) {
    // console.log('Animation Started:', this.CurrentSkin, entry.animation.name, entry);
  }

  protected animationCompleted(entry: spine.TrackEntry) {
    // console.log('Animation Completed:', this.CurrentSkin, entry.animation.name, entry);

    if (entry.animation.name == CharacterAnimation.Blink) this.randomBlink();
  }

  protected animationEnded(entry: spine.TrackEntry) {
    // console.log('Animation Ended:', this.CurrentSkin, entry.animation.name, entry);

    if (entry.animation.name == CharacterAnimation.HeroRampIn) this.emit(CharacterSpine.events.RampInEnded);
    else if (entry.animation.name == CharacterAnimation.HeroRampAir) this.emit(CharacterSpine.events.RampAirEnded);
    else if (entry.animation.name == CharacterAnimation.HeroRampOut) this.emit(CharacterSpine.events.RampOutEnded);
    else if (entry.animation.name == CharacterAnimation.Warning) this.emit(CharacterSpine.events.WarningEnded);
  }

  protected animationInterrupted(entry: spine.TrackEntry) {
    // console.log('Animation Interrupted:', this.CurrentSkin, entry.animation.name, entry);
  }

  protected animationDisposed(entry: spine.TrackEntry) {
    // console.log('Animation Disposed:', this.CurrentSkin, entry.animation.name, entry);
  }

  protected animationEvent(entry: spine.TrackEntry, event: spine.Event) {
    // console.log(entry.animation, event.stringValue);
  }

  protected randomBlink() {
    let nextBlink = Phaser.Math.Between(1000, 5000);
    this.scene.time.delayedCall(nextBlink, () => {
      this.setAnimation(1, CharacterAnimation.Blink, false);
    });
    // this.addAnimation(1, CharacterAnimation.Blink, false, nextBlink);
  }
}
